// src/components/ReservationView.tsx
import React, { useState, useEffect } from 'react';
import { Calendar, CreditCard, AlertCircle, CheckCircle2, ChevronRight, Loader2, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { db, appId } from '../constants/firebase';
import { formatDateEU } from '../utils/formatters';

interface ReservationViewProps {
    stringerId: string;
}

export default function ReservationView({ stringerId }: ReservationViewProps) {
    const [rackets, setRackets] = useState<any[]>([]);
    const [reservations, setReservations] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Klantgegevens
    const [customerData, setCustomerData] = useState({
        firstName: '', lastName: '', email: '', phone: ''
    });

    // Selecties (Max 2)
    const [selection1, setSelection1] = useState({ racketId: '', startDate: '' });
    const [selection2, setSelection2] = useState({ racketId: '', startDate: '' });
    const [wantsSecondRacket, setWantsSecondRacket] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!stringerId) return;
            setIsLoading(true);
            try {
                // 1. Rackets ophalen
                const racketsSnap = await getDocs(collection(db, 'artifacts', appId, 'users', stringerId, 'testrackets'));
                const racketsData = racketsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
                setRackets(racketsData.filter((r: any) => r.status === 'available' || r.status === 'rented')); 

                // 2. Bestaande reserveringen ophalen
                const resQuery = query(
                    collection(db, 'artifacts', appId, 'users', stringerId, 'reservations'),
                    where('status', 'in', ['pending_payment', 'confirmed'])
                );
                const resSnap = await getDocs(resQuery);
                const resData = resSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
                setReservations(resData);
            } catch (error) {
                console.error("Fout bij ophalen gegevens:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [stringerId]);

    const getReturnDate = (start: string) => {
        if (!start) return '';
        const date = new Date(start);
        date.setDate(date.getDate() + 7);
        return date.toISOString().split('T')[0];
    };

    // Unieke check per racket & datum
    const checkAvailability = (racketId: string, startDate: string) => {
        if (!startDate || !racketId) return true;
        
        const start = new Date(startDate).getTime();
        const end = new Date(getReturnDate(startDate)).getTime();

        const hasOverlap = reservations.some(res => {
            if (res.racketId !== racketId) return false;
            const resStart = new Date(res.startDate).getTime();
            const resEnd = new Date(res.returnDate).getTime();
            return (start < resEnd) && (end > resStart);
        });

        return !hasOverlap;
    };

    const isAvailable1 = checkAvailability(selection1.racketId, selection1.startDate);
    const isAvailable2 = wantsSecondRacket ? checkAvailability(selection2.racketId, selection2.startDate) : true;
    
    // Validatie of we mogen submitten
    const canSubmit = () => {
        if (!selection1.racketId || !selection1.startDate || !isAvailable1) return false;
        if (wantsSecondRacket && (!selection2.racketId || !selection2.startDate || !isAvailable2)) return false;
        if (!customerData.firstName || !customerData.lastName || !customerData.email || !customerData.phone) return false;
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit()) return;

        setIsSubmitting(true);
        try {
            const commonData = {
                status: 'pending_payment',
                customer: customerData,
                createdAt: Date.now()
            };

            // Sla racket 1 op
            await addDoc(collection(db, 'artifacts', appId, 'users', stringerId, 'reservations'), {
                ...commonData,
                racketId: selection1.racketId,
                startDate: selection1.startDate,
                returnDate: getReturnDate(selection1.startDate)
            });

            // Sla racket 2 op indien geselecteerd
            if (wantsSecondRacket && selection2.racketId) {
                await addDoc(collection(db, 'artifacts', appId, 'users', stringerId, 'reservations'), {
                    ...commonData,
                    racketId: selection2.racketId,
                    startDate: selection2.startDate,
                    returnDate: getReturnDate(selection2.startDate)
                });
            }

            alert("Reservering succesvol opgeslagen! (In Stap 4 koppelen we hier Mollie aan vast).");
            
        } catch (error) {
            console.error("Fout bij reserveren:", error);
            alert("Er is iets misgegaan. Probeer het opnieuw.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderRacketTiles = (currentSelection: any, setSelection: any, excludeRacketId: string = '') => {
        return (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {rackets.filter(r => r.id !== excludeRacketId).map(racket => {
                    const isSelected = currentSelection.racketId === racket.id;
                    const imageUrl = racket.imageUrl || racket.image || null;

                    return (
                        <div 
                            key={racket.id} 
                            onClick={() => setSelection({ ...currentSelection, racketId: racket.id })}
                            className={`relative bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${isSelected ? 'border-indigo-600 shadow-md ring-4 ring-indigo-50' : 'border-slate-100 hover:border-indigo-300'}`}
                        >
                            {isSelected && (
                                <div className="absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1 z-10">
                                    <CheckCircle2 className="w-4 h-4" />
                                </div>
                            )}
                            
                            <div className="aspect-[3/4] rounded-xl bg-slate-50 mb-4 flex flex-col items-center justify-center overflow-hidden border border-slate-100 relative">
                                {imageUrl ? (
                                    <img src={imageUrl} alt={`${racket.brand} ${racket.model}`} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="text-slate-300 flex flex-col items-center">
                                        <ImageIcon className="w-10 h-10 mb-2 opacity-50" />
                                        <span className="text-[10px] uppercase font-bold tracking-wider">Geen Foto</span>
                                    </div>
                                )}
                            </div>
                            
                            <div>
                                <p className="font-bold text-slate-800 text-sm leading-tight">{racket.brand}</p>
                                <p className="text-xs text-slate-500 truncate">{racket.model}</p>
                                {racket.grip && <p className="text-[10px] font-medium text-slate-400 mt-1 bg-slate-100 inline-block px-2 py-0.5 rounded-full">Grip {racket.grip}</p>}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-indigo-600">
                <Loader2 className="w-10 h-10 animate-spin" />
                <span className="ml-3 font-bold">Testrackets inladen...</span>
            </div>
        );
    }

    const totalBorg = wantsSecondRacket && selection2.racketId ? 50 : 25;

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto bg-white rounded-[2rem] shadow-xl overflow-hidden border border-slate-100">
                <div className="bg-indigo-600 px-6 py-10 text-white text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-4 opacity-90" />
                    <h2 className="text-3xl font-bold">Testracket Reserveren</h2>
                    <p className="mt-2 text-indigo-100">Kies je racket, prik een datum en test hem 7 dagen uit.</p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 md:p-10 space-y-10">
                    
                    {/* SECTIE 1: EERSTE RACKET */}
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">1</div>
                            <h3 className="font-bold text-xl text-slate-800">Kies een testracket</h3>
                        </div>
                        
                        {renderRacketTiles(selection1, setSelection1)}

                        {selection1.racketId && (
                            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 animate-in fade-in slide-in-from-top-4">
                                <label className="block text-sm font-bold text-slate-700 mb-2">Vanaf welke datum wil je dit racket testen?</label>
                                <input 
                                    type="date" 
                                    min={new Date().toISOString().split('T')[0]}
                                    value={selection1.startDate}
                                    onChange={e => setSelection1({...selection1, startDate: e.target.value})}
                                    className="w-full p-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500"
                                />
                                {selection1.startDate && !isAvailable1 && (
                                    <p className="text-sm font-bold text-red-600 mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> Dit racket is op deze datum helaas al bezet.</p>
                                )}
                                {selection1.startDate && isAvailable1 && (
                                    <p className="text-sm text-emerald-600 font-bold mt-2 flex items-center">
                                        <CheckCircle2 className="w-4 h-4 mr-1" /> Beschikbaar! Retour uiterlijk: {formatDateEU(getReturnDate(selection1.startDate))}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* SECTIE 2: TWEEDE RACKET (OPTIONEEL) */}
                    <div>
                        {!wantsSecondRacket ? (
                            <button 
                                type="button"
                                onClick={() => setWantsSecondRacket(true)}
                                className="w-full py-4 border-2 border-dashed border-slate-300 rounded-2xl text-slate-500 font-bold hover:bg-slate-50 hover:border-indigo-400 hover:text-indigo-600 transition flex items-center justify-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Nog een racket testen toevoegen (+ €25 borg)
                            </button>
                        ) : (
                            <div className="border-t-2 border-dashed border-slate-200 pt-8 animate-in fade-in">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">2</div>
                                        <h3 className="font-bold text-xl text-slate-800">Tweede racket (Optioneel)</h3>
                                    </div>
                                    <button type="button" onClick={() => { setWantsSecondRacket(false); setSelection2({ racketId: '', startDate: ''}); }} className="text-red-500 text-sm font-bold flex items-center hover:bg-red-50 px-3 py-1.5 rounded-lg transition">
                                        <Trash2 className="w-4 h-4 mr-1"/> Verwijder
                                    </button>
                                </div>

                                {renderRacketTiles(selection2, setSelection2, selection1.racketId)}

                                {selection2.racketId && (
                                    <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 animate-in fade-in slide-in-from-top-4">
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Vanaf welke datum wil je dit racket testen?</label>
                                        <input 
                                            type="date" 
                                            min={new Date().toISOString().split('T')[0]}
                                            value={selection2.startDate}
                                            onChange={e => setSelection2({...selection2, startDate: e.target.value})}
                                            className="w-full p-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500"
                                        />
                                        {selection2.startDate && !isAvailable2 && (
                                            <p className="text-sm font-bold text-red-600 mt-2 flex items-center"><AlertCircle className="w-4 h-4 mr-1" /> Dit racket is op deze datum helaas al bezet.</p>
                                        )}
                                        {selection2.startDate && isAvailable2 && (
                                            <p className="text-sm text-emerald-600 font-bold mt-2 flex items-center">
                                                <CheckCircle2 className="w-4 h-4 mr-1" /> Beschikbaar! Retour uiterlijk: {formatDateEU(getReturnDate(selection2.startDate))}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* SECTIE 3: KLANT GEGEVENS */}
                    <div className="border-t-2 border-slate-100 pt-8">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold">3</div>
                            <h3 className="font-bold text-xl text-slate-800">Jouw gegevens</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Voornaam</label>
                                <input type="text" required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500" value={customerData.firstName} onChange={e => setCustomerData({...customerData, firstName: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Achternaam</label>
                                <input type="text" required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500" value={customerData.lastName} onChange={e => setCustomerData({...customerData, lastName: e.target.value})} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">E-mailadres</label>
                                <input type="email" required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500" value={customerData.email} onChange={e => setCustomerData({...customerData, email: e.target.value})} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Telefoonnummer</label>
                                <input type="tel" required className="w-full p-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-indigo-500" value={customerData.phone} onChange={e => setCustomerData({...customerData, phone: e.target.value})} />
                            </div>
                        </div>
                    </div>

                    {/* BORG INFO & SUBMIT */}
                    <div className="pt-4 space-y-6">
                        <div className="bg-blue-50/50 p-5 rounded-2xl border border-blue-100 flex items-start">
                            <AlertCircle className="w-6 h-6 text-blue-500 mr-3 shrink-0" />
                            <div className="text-sm text-blue-900 leading-relaxed">
                                <strong>Borg regeling:</strong> Je betaalt <strong>€ 25 borg per racket</strong> (totaal € {totalBorg}). Besluit je na het testen een racket via ons aan te schaffen? Dan krijg je de betaalde borg volledig als korting op je aankoop!
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            disabled={!canSubmit() || isSubmitting}
                            className={`w-full py-5 rounded-2xl shadow-xl transition-all flex items-center justify-center font-bold text-lg
                                ${canSubmit() ? 'bg-indigo-600 hover:bg-indigo-700 hover:-translate-y-1 hover:shadow-2xl text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                        >
                            {isSubmitting ? (
                                <><Loader2 className="w-6 h-6 mr-2 animate-spin" /> Bestelling verwerken...</>
                            ) : (
                                <><CreditCard className="w-6 h-6 mr-2" /> Reserveer & Betaal (€ {totalBorg})</>
                            )}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}